import {inject, Injectable, OnDestroy} from '@angular/core';
import {OnlineOfflineService} from "../onlineOffline/online-offline.service";
import {StorageService} from "../storage/storage.service";
import {AuthService} from "../auth/auth.service";
import {FailedRequestEntity} from "./dirty-data.interceptor";
import {ErrorService} from "../error/error.service";
import {LoggerService} from "../logging/logger.service";
import {ObservabilityService} from "../observability/observability.service";
import {ReadRetryService} from "../cache/read-retry.service";


@Injectable({
  providedIn: 'root'
})
export class DirtyDataWriterService implements OnDestroy {
  private readonly onlineOfflineService = inject(OnlineOfflineService);
  private readonly storageService = inject(StorageService);
  private readonly authenticationServer = inject(AuthService);
  private readonly errorService = inject(ErrorService);
  private readonly logger = inject(LoggerService);
  private readonly observabilityService = inject(ObservabilityService);
  // Safe as a field initialiser: ReadRetryService has no dependencies,
  // and the dependency has to point this way (see its class comment).
  private readonly readRetryService = inject(ReadRetryService);

  private intervalId: any;
  private online: boolean = false;
  private processInFlight: boolean = false;
  /** A reconnect happened and its read retry has not yet been released. */
  private readRetryPending: boolean = false;
  /**
   * How long the read retry waits for the write queue to drain before
   * the ordering guarantee is given up on. A worker that never reports
   * completion must not strand the Retry button, which now defers to
   * this release rather than firing straight away (HEC-304).
   */
  private static readonly READ_RETRY_HOLD_TIMEOUT_MS = 10000;
  private readRetryHoldTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly worker: Worker;
  /** Worker callbacks run strictly one after another (HEC-371). */
  private callbackChain: Promise<void> = Promise.resolve();

  constructor() {
    this.worker = new Worker('/assets/dirty-data-writer.js');
    this.worker.onmessage = event => this.onWorkerMessage(event.data);
  }

  /**
   * Serialises worker callbacks so a late failure cannot read a queue
   * entry while the success callback for the same item is removing it,
   * and `completed` is handled after every item callback of its batch.
   * handleCallBack reports its own errors; the trailing catch keeps a
   * throw from that reporting (or the log call) from leaving the chain
   * rejected, which would skip every later callback and wedge
   * `processInFlight` until the app restarts.
   */
  private onWorkerMessage(data: any): void {
    this.callbackChain = this.callbackChain.then(async () => {
      await this.handleCallBack(data);
      this.logger.info('Callback processing finished');
    }).catch(() => undefined);
  }

  async initialize() {
    const isOnline = await this.onlineOfflineService.isOnline();

    this.observabilityService.trackProcessStarted('DirtyDataWriter', {
      online: isOnline
    });

    this.registerToEvents(this.onlineOfflineService);
    this.online = isOnline;

    this.observabilityService.trackServiceInitialized('DirtyDataWriter', {
      online: isOnline
    });
  }

  ngOnDestroy() {
    this.observabilityService.trackProcessStopped('DirtyDataWriter', {
      processInFlight: this.processInFlight,
      online: this.online
    });

    this.stopPeriodicTask();
    this.clearReadRetryHoldTimer();
    if (this.worker) {
      this.worker.terminate();
    }
  }

  public async findFailedRequest(failedRequest: FailedRequestEntity): Promise<FailedRequestEntity | null> {
    return await this.storageService.getItem<FailedRequestEntity>(failedRequest.id, failedRequest.type);
  }

  public async storeFailedRequest(failedRequest: FailedRequestEntity): Promise<void> {
    await this.storageService.setItem(failedRequest.id, failedRequest);

  }

  private async removeFailedRequest(failedRequest: FailedRequestEntity) {
    await this.storageService.removeItem(failedRequest.id, failedRequest.type);
  }

  private registerToEvents(onlineOfflineService: OnlineOfflineService) {
    onlineOfflineService.connectionChanged.subscribe(online => {
      this.observabilityService.trackDataSyncConnectionChanged(
        this.online ? 'online' : 'offline',
        online ? 'online' : 'offline',
        {
          previousState: this.online,
          processInFlight: this.processInFlight
        }
      );

      this.online = online;
      if (online) {
        this.startPeriodicTask();
        // HEC-304: replay queued offline writes at once rather than on
        // the first 30 s tick, and retry failed reads only once that
        // replay has finished (releaseReadRetry). Refetching first would
        // overwrite a cached week holding an optimistically saved event
        // with server data that does not yet contain it.
        this.readRetryPending = true;
        // The hold covers the manual Retry button too: without it the
        // strip's Retry refetches immediately and can overwrite the
        // optimistically cached write this replay is about to send.
        this.readRetryService.hold();
        this.clearReadRetryHoldTimer();
        this.readRetryHoldTimer = setTimeout(
          () => this.releaseReadRetry(),
          DirtyDataWriterService.READ_RETRY_HOLD_TIMEOUT_MS);
        this.postDirtyDataToWorker();
      } else {
        this.stopPeriodicTask();
      }
    });
  }

  private async handleCallBack(data: any) {

    try {
      if (data.completed) {
        this.logger.info('Worker has completed all tasks');
        this.processInFlight = false;

        const queueSize = await this.getQueueSize();
        this.observabilityService.trackDataSyncBatchCompleted(`batch_${Date.now()}`, queueSize, {
          remainingQueueSize: queueSize
        });
        this.releaseReadRetry();
      }  else {
        const failedRequest = data?.data ? await this.findFailedRequest(new FailedRequestEntity(data.data.url, data.data.method, data.data.body)) : null;

        if (failedRequest) {
          if (data.status === 200) {
            await this.removeFailedRequest(failedRequest);

            this.observabilityService.trackDataSyncRequestSucceeded(failedRequest.id, {
              url: failedRequest.url,
              method: failedRequest.method,
              retries: failedRequest.retries,
              processingTime: this.calculateProcessingTime(failedRequest.startTime)
            });

          } else if (data.status > 200) {
            // The request was successful, but the server returned an application error
            failedRequest.retries++;

            if (failedRequest.retries > 50) {
              await this.removeFailedRequest(failedRequest);
              this.registerErrorMessage(failedRequest.body);

              this.observabilityService.trackDataSyncRequestAbandoned(failedRequest.id, {
                url: failedRequest.url,
                method: failedRequest.method,
                retries: failedRequest.retries,
                status: data.status,
                statusText:  data.statusText,
                reason: 'max_retries_exceeded'
              });
            } else {
              await this.storeFailedRequest(failedRequest);

              this.observabilityService.trackDataSyncRequestRetryScheduled(failedRequest.id, failedRequest.retries, {
                url: failedRequest.url,
                method: failedRequest.method,
                status: data.status,
                statusText: data.statusText
              });
            }
          } else {
            this.logger.info(data.statusText);

            this.observabilityService.trackDataSyncRequestFailed(`Request failed with status ${data.status}`, {
              requestId: failedRequest.id,
              url: failedRequest.url,
              method: failedRequest.method,
              retries: failedRequest.retries,
              status: data.status,
              statusText: data.statusText
            });
          }
        }
      }

    } catch (error) {
      this.logger.error('Error processing callback', error, data);
      this.observabilityService.captureException(error, {
        context: 'DataSync.CallbackProcessingError',
        data: data
      });
    }
  }

  private registerErrorMessage(body: any) {
    if (!body) return;

    const message = body instanceof FormData
      ? 'Unable to upload photo'
      : `Unable to upload ${body.type}`;

    this.errorService.registerError(message);
  }

  /**
   * Calculates the processing time in seconds from the startTime.
   * Handles cases where startTime may be null, undefined, a string (from JSON
   * deserialisation), or not a valid Date object.
   */
  private calculateProcessingTime(startTime: Date | string | null | undefined): number | string {
    if (!startTime) {
      return 'N/A';
    }

    try {
      // Handle string dates (from JSON deserialisation)
      const dateValue = startTime instanceof Date ? startTime : new Date(startTime);

      // Check if the date is valid
      if (isNaN(dateValue.getTime())) {
        return 'N/A';
      }

      return Math.floor((Date.now() - dateValue.getTime()) / 1000);
    } catch {
      return 'N/A';
    }
  }

  private postDirtyDataToWorker(): void {
    if (!this.authenticationServer.isLoggedIn()) {
      this.logger.debug('Not processing dirty data: not logged in');
      // Nothing to replay, and no authenticated read to retry either.
      // Releasing synchronously here, inside connectionChanged's
      // Subject.next loop, is safe: findAll / findWithQuery evaluate
      // shouldRefresh() only after their own first await on the cache,
      // so this cannot beat the other subscribers to the `online` flag.
      this.releaseReadRetry();
      return;
    }
    if (!this.online) {
      // Unreachable from the reconnect path (registerToEvents assigns
      // `online` before calling in); the periodic tick can land here.
      this.logger.debug('Not processing dirty data: offline');
      return;
    }
    if (this.processInFlight) {
      // A batch is mid-replay: leave readRetryPending set so that
      // batch's `completed` callback releases it, never this branch -
      // retrying reads now is the exact race the sequencing prevents.
      this.logger.debug('Not processing dirty data: a batch is in flight');
      return;
    }
    // always drain post requests
    this.drainDirtyDataQueue().then((postItemsQueued: boolean) => {
      if (postItemsQueued) {
        this.logger.debug('Processing dirty data');
      } else {
        this.releaseReadRetry();
      }
    });
  }

  /**
   * Fires the reconnect read retry once, and only while one is
   * pending: from the empty-queue and logged-out paths directly, and
   * from the worker's `completed` callback when a batch was posted,
   * and from READ_RETRY_HOLD_TIMEOUT_MS if a worker never reports
   * completion at all - reads must not be stranded, and neither must
   * a Retry tap that was deferred behind the hold.
   */
  private releaseReadRetry(): void {
    if (!this.readRetryPending) {
      return;
    }
    this.readRetryPending = false;
    this.clearReadRetryHoldTimer();
    // release(), not retryIfFailed(): it ends the hold and then runs
    // whichever retry was asked for during it - the user's deferred
    // tap if there was one, otherwise the reconnect's own.
    this.readRetryService.release();
  }

  private clearReadRetryHoldTimer(): void {
    if (this.readRetryHoldTimer !== undefined) {
      clearTimeout(this.readRetryHoldTimer);
      this.readRetryHoldTimer = undefined;
    }
  }

  private async drainDirtyDataQueue(): Promise<boolean> {
    // Claimed before the first await so a burst of triggers lets only
    // one drain through (HEC-371); cleared below unless a batch is posted.
    this.processInFlight = true;
    let batchPosted = false;
    const startTime = Date.now();

    try {
      let failedRequests: FailedRequestEntity[] = await this.storageService.getAllItems(`FailedRequestEntity`);

      if (failedRequests && failedRequests.length != 0) {
        this.observabilityService.trackDataSyncQueueDrainStarted({
          queueSize: failedRequests.length,
          online: this.online,
          authenticated: this.authenticationServer.isLoggedIn()
        });

        this.logger.info(`Found ${failedRequests.length} failed requests to process`);

        // sort list by time from oldest to newest
        failedRequests.sort((a, b) => a.time - b.time);

        try {
          this.worker.postMessage({
            items: failedRequests,
            authToken: this.authenticationServer.getAuthToken()
          });

          batchPosted = true;
          return true;
        } catch (error) {
          this.logger.error('Error posting dirty data to worker', error);

          this.observabilityService.captureException(error, {
            context: 'DataSync.WorkerPostError',
            batchSize: failedRequests.length
          });
        }
      }

      return false;
    } catch (error) {
      this.observabilityService.captureException(error, {
        context: 'DataSync.QueueDrainError',
        processingTime: Date.now() - startTime
      });
      return false;
    } finally {
      if (!batchPosted) {
        this.processInFlight = false;
      }
    }
  }

  private async getQueueSize(): Promise<number> {
    try {
      const failedRequests = await this.storageService.getAllItems(`FailedRequestEntity`);
      return failedRequests?.length ?? 0;
    } catch (error) {
      this.observabilityService.captureException(error, {
        context: 'DataSync.QueueSizeError'
      });
      return 0;
    }
  }

  startPeriodicTask(): void {
    if (this.intervalId) {
      return;
    }
    this.logger.debug('Starting dirty data periodic task');

    this.observabilityService.trackProcessStarted('DirtyDataWriter', {
      online: this.online,
      processInFlight: this.processInFlight
    });

    this.intervalId = setInterval(() => {
      this.postDirtyDataToWorker();
    }, 30000); // 30 seconds
  }

  stopPeriodicTask(): void {
    if (this.intervalId) {
      this.logger.info('Stopping dirty data periodic task');

      this.observabilityService.trackProcessStopped('DirtyDataWriter', {
        online: this.online,
        processInFlight: this.processInFlight
      });

      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
